import { useEffect, useState } from "react";
import { Fingerprint, Laptop, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../../../api/client";
import { Button, Field, TextInput } from "../../../components/ui";

interface Passkey { id: string; nickname: string; created_at: string; last_used_at: string | null; flagged: boolean }
interface Device { id: string; label: string | null; ip_prefix: string | null; created_at: string; last_used_at: string | null; expires_at: string }
const BASE = "/admin/me/security";
const when = (v: string | null) => (v ? new Date(v).toLocaleString() : "never");

function CodeBox({ code, setCode, id }: { code: string; setCode: (v: string) => void; id: string }) {
  return <Field label="Fresh 6-digit code (or a backup code)" htmlFor={id}><TextInput id={id} value={code} onChange={(e) => setCode(e.target.value.trim())} autoComplete="one-time-code" data-testid={id} /></Field>;
}

export function Passkeys() {
  const [items, setItems] = useState<Passkey[]>([]);
  const [rpId, setRpId] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState("");
  const load = () => api.get<{ passkeys: Passkey[]; rp_id: string }>(`${BASE}/passkeys`).then((r) => { setItems(r.passkeys); setRpId(r.rp_id); });
  useEffect(() => { void load().catch((e) => toast.error(errorMessage(e))); }, []);
  const run = async (key: string, fn: () => Promise<void>) => { setBusy(key); try { await fn(); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(""); } };
  const add = () => run("add", async () => {
    const { startRegistration } = await import("@simplewebauthn/browser");
    const o = await api.post<{ challenge_id: string; options: Parameters<typeof startRegistration>[0]["optionsJSON"] }>(`${BASE}/passkeys/options`, { code });
    setCode("");
    const credential = await startRegistration({ optionsJSON: o.options });
    await api.post(`${BASE}/passkeys`, { challenge_id: o.challenge_id, credential, nickname: name || "My passkey" });
    setName(""); toast.success("Passkey added"); await load();
  });
  const rename = (p: Passkey) => run(p.id, async () => {
    const nickname = window.prompt("Passkey name", p.nickname)?.trim();
    if (!nickname) return;
    await api.patch(`${BASE}/passkeys/${p.id}`, { nickname }); await load();
  });
  const remove = (p: Passkey) => run(p.id, async () => {
    setItems((await api.post<{ passkeys: Passkey[] }>(`${BASE}/passkeys/${p.id}/remove`, { code })).passkeys); setCode(""); toast.success("Passkey removed");
  });
  return (
    <section className="glass rounded-[20px] p-6" data-testid="security-passkeys-card">
      <h2 className="flex items-center gap-2 text-lg font-medium text-white"><Fingerprint className="h-5 w-5 text-signal" />Passkeys</h2>
      <p className="mt-1 text-sm text-zinc-400">Sign in with Face ID, Touch ID, a fingerprint or a security key. Password + authenticator always keeps working.</p>
      <p className="mt-2 rounded-xl border border-amber-400/30 bg-amber-400/5 p-3 text-xs text-amber-100" data-testid="security-passkeys-domain-note">
        Passkeys belong to the address you add them on (now: <span className="font-mono">{rpId}</span>). Ones added on Preview won't work on gamedaytime.com, so add them again after publishing.
      </p>
      <ul className="mt-4 divide-y divide-white/5" data-testid="settings-passkeys-list">
        {items.map((p) => (
          <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm" data-testid={`passkey-${p.id}`}>
            <span><span className="font-medium text-white">{p.nickname}</span>{p.flagged && <span className="ml-2 text-xs text-red-300">flagged: possible copy</span>}
              <span className="block text-xs text-zinc-500">Added {when(p.created_at)} · last used {when(p.last_used_at)}</span></span>
            <span className="flex gap-1">
              <Button variant="ghost" className="h-8 px-2" aria-label={`Rename ${p.nickname}`} onClick={() => rename(p)} data-testid={`passkey-rename-${p.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" className="h-8 px-2" aria-label={`Remove ${p.nickname}`} disabled={code.length < 6} loading={busy === p.id} onClick={() => remove(p)} data-testid={`passkey-remove-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
            </span>
          </li>
        ))}
        {items.length === 0 && <li className="py-2.5 text-sm text-zinc-500">No passkeys yet.</li>}
      </ul>
      <div className="mt-4 grid max-w-2xl gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <Field label="Name" htmlFor="passkey-name"><TextInput id="passkey-name" value={name} maxLength={60} placeholder="Mike's iPhone" onChange={(e) => setName(e.target.value)} data-testid="passkey-name-input" /></Field>
        <CodeBox code={code} setCode={setCode} id="passkey-code-input" />
        <Button onClick={add} loading={busy === "add"} disabled={code.length < 6} data-testid="security-add-passkey">Add a passkey</Button>
      </div>
      <p className="mt-2 text-xs text-zinc-500">Removing a passkey also needs a fresh code: enter it, then press the bin.</p>
    </section>
  );
}

export function TrustedDevices() {
  const [items, setItems] = useState<Device[]>([]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get<{ devices: Device[] }>(`${BASE}/devices`).then((r) => setItems(r.devices)).catch((e) => toast.error(errorMessage(e))); }, []);
  const run = async (key: string, path: string) => {
    setBusy(key);
    try { setItems((await api.post<{ devices: Device[] }>(path, { code })).devices); setCode(""); toast.success("Removed"); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(""); }
  };
  return (
    <section className="glass rounded-[20px] p-6" data-testid="security-devices-card">
      <h2 className="flex items-center gap-2 text-lg font-medium text-white"><Laptop className="h-5 w-5 text-signal" />Remembered devices</h2>
      <p className="mt-1 text-sm text-zinc-400">These browsers skip the password for 30 days. They still need your authenticator code. They're removed automatically when your password, authenticator or backup codes change.</p>
      <ul className="mt-4 divide-y divide-white/5" data-testid="settings-trusted-devices-list">
        {items.map((d) => (
          <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm" data-testid={`device-${d.id}`}>
            <span><span className="font-medium text-white">{d.label ?? "Browser"}</span>{d.ip_prefix && <span className="ml-2 font-mono text-xs text-zinc-500">{d.ip_prefix}</span>}
              <span className="block text-xs text-zinc-500">Added {when(d.created_at)} · last used {when(d.last_used_at)} · expires {when(d.expires_at)}</span></span>
            <Button variant="ghost" className="h-8 px-2" aria-label={`Remove ${d.label}`} disabled={code.length < 6} loading={busy === d.id} onClick={() => run(d.id, `${BASE}/devices/${d.id}/remove`)} data-testid={`device-remove-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
          </li>
        ))}
        {items.length === 0 && <li className="py-2.5 text-sm text-zinc-500">No remembered devices.</li>}
      </ul>
      <div className="mt-4 flex max-w-xl flex-wrap items-end gap-3">
        <div className="flex-1"><CodeBox code={code} setCode={setCode} id="device-code-input" /></div>
        <Button variant="secondary" disabled={code.length < 6 || items.length === 0} loading={busy === "all"} onClick={() => run("all", `${BASE}/devices/remove-all`)} data-testid="security-remove-all-devices">Remove all</Button>
      </div>
    </section>
  );
}
